/**
 * Phone/form-call leads feed table for the Location Detail page.
 *
 * Sourced from ghl_form_call_lead_checks. Read-only — there is no per-contact
 * detail page for form-call leads, so rows are not clickable.
 */

import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { formatDateTimeParts, getPhoneCallStatusColor } from '@/lib/ghl-qualifier'
import type { PhoneCallLeadFeedRow } from '@/hooks/useGHLQualifierDashboard'

interface Props {
  readonly data: readonly PhoneCallLeadFeedRow[]
  readonly isLoading?: boolean
  readonly onRowClick: (id: string) => void
}

/** Human label for the conversion events the worker actually uploaded. */
const EVENT_LABELS: Record<string, string> = {
  phone_call: 'Phone call',
  form_submit: 'Form submit',
}

export function PhoneCallLeadsTable({ data, isLoading, onRowClick }: Props) {
  const colSpan = 4
  return (
    <div className="rounded-md border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Contact</TableHead>
            <TableHead>Status</TableHead>
            <TableHead>Uploaded</TableHead>
            <TableHead className="text-right">Created</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {isLoading
            ? Array.from({ length: 5 }).map((_, i) => (
                <TableRow key={`skeleton-${i}`}>
                  <TableCell colSpan={colSpan}>
                    <Skeleton className="h-5 w-full" />
                  </TableCell>
                </TableRow>
              ))
            : data.map((row) => {
                const status = getPhoneCallStatusColor(row.status)
                const displayName =
                  row.contact_name && row.contact_name.trim().length > 0
                    ? row.contact_name
                    : row.contact_id
                const subline = row.contact_phone ?? row.contact_email ?? row.contact_id
                const uploaded = row.events_uploaded ?? []

                return (
                  <TableRow
                    key={row.id}
                    className="cursor-pointer hover:bg-muted/40"
                    onClick={() => onRowClick(row.id)}
                  >
                    <TableCell>
                      <div className="flex flex-col">
                        <span className="font-medium">{displayName}</span>
                        <span className="text-xs text-muted-foreground">{subline}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-col gap-0.5">
                        <Badge className={`${status.bg} ${status.text} hover:${status.bg} w-fit`}>
                          {status.label}
                        </Badge>
                        {row.status === 'failed' && row.error_message ? (
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <span className="max-w-[160px] cursor-help truncate text-[11px] text-muted-foreground">
                                  {row.error_message}
                                </span>
                              </TooltipTrigger>
                              <TooltipContent side="top" className="max-w-sm">
                                {row.error_message}
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        ) : null}
                      </div>
                    </TableCell>
                    <TableCell>
                      {uploaded.length > 0 ? (
                        <div className="flex flex-wrap gap-1">
                          {uploaded.map((evt) => (
                            <Badge key={evt} variant="secondary" className="w-fit">
                              {EVENT_LABELS[evt] ?? evt}
                            </Badge>
                          ))}
                        </div>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </TableCell>
                    <TableCell className="text-right text-sm">
                      {(() => {
                        const parts = formatDateTimeParts(row.created_at)
                        if (!parts) {
                          return <span className="text-muted-foreground">—</span>
                        }
                        return (
                          <div className="flex flex-col leading-tight">
                            <span>{parts.date}</span>
                            <span className="text-xs text-muted-foreground">{parts.time}</span>
                          </div>
                        )
                      })()}
                    </TableCell>
                  </TableRow>
                )
              })}
          {!isLoading && data.length === 0 && (
            <TableRow>
              <TableCell colSpan={colSpan} className="py-12 text-center text-muted-foreground">
                No phone-call leads in this time range.
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </div>
  )
}
